import type { TravelConfig } from '../config/TravelConfig';
import { TravelMode, WarpSegment, type TravelRoute } from './TravelTypes';

/**
 * Builds a TravelRoute from pathfinding hex output.
 * Assigns per-hop durations and warp segment kinds.
 */
export function buildTravelRoute(
  hexes: readonly { q: number; r: number }[],
  mode: TravelMode,
  config: TravelConfig,
): TravelRoute {
  const hopCount = hexes.length - 1;
  const durations = new Float32Array(hopCount);
  const kinds = new Uint8Array(hopCount);

  if (mode !== TravelMode.Warp) {
    const hd = mode === TravelMode.Reveal ? config.reveal.hopDuration : config.normal.hopDuration;
    for (let i = 0; i < hopCount; i++) durations[i] = hd;
    return { hexes, segmentDurations: durations, segmentKinds: kinds, totalSeconds: hd * hopCount, warpRanges: [] };
  }
  const wc = config.warp;
  const warmup = Math.min(wc.warmupHops, hopCount);
  const cooldown = Math.min(wc.cooldownHops, Math.max(0, hopCount - warmup));
  const warpLen = Math.max(0, hopCount - warmup - cooldown);
  for (let i = 0; i < hopCount; i++) {
    if (i < warmup) { durations[i] = wc.normalHopDuration; kinds[i] = WarpSegment.Warmup; }
    else if (i < warmup + warpLen) { durations[i] = wc.warpHopDuration; kinds[i] = WarpSegment.Warp; }
    else { durations[i] = wc.normalHopDuration; kinds[i] = WarpSegment.Cooldown; }
  }
  let total = 0;
  for (let i = 0; i < hopCount; i++) total += durations[i];
  return {
    hexes, segmentDurations: durations, segmentKinds: kinds, totalSeconds: total,
    warpRanges: warpLen > 0 ? [{ startIdx: warmup, endIdx: warmup + warpLen }] : [],
  };
}
